import { ActiveNavLink } from "components/common/ActiveNavLink";
import { HierarchyContext } from "contexts/HierarchyContext";
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  useTransition,
} from "react";
import { DashCircleDotted } from "react-bootstrap-icons";
import { getHierarchyBaseUrl } from "../../contexts/hierarchy/getHierarchyBaseUrl";
import { eHierarchy } from "../../functions/api/enums";

interface IRecentLink {
  id: string;
  name: string;
  type: eHierarchy;
  path: string;
}

/**
 * Type guard to validate an object as IRecentLink
 *
 * @param obj - The object to validate as IRecentLink
 * @returns boolean
 */
export const isIRecentLink = (obj: unknown): obj is IRecentLink => {
  return (
    typeof obj === "object" &&
    obj !== null &&
    typeof (obj as IRecentLink).id === "string" &&
    typeof (obj as IRecentLink).name === "string" &&
    typeof (obj as IRecentLink).type === "string" &&
    typeof (obj as IRecentLink).path === "string"
  );
};

/**
 * Shows a list of recently-viewed hierarchies stored in localStorage, allowing
 * removal of individual entries and navigation to saved hierarchy links.
 *
 * @returns The `RecentLinks` component rendering recent hierarchy links.
 */
export const RecentLinks: React.FC = () => {
  const [_isPending, startTransition] = useTransition();
  const hierarchyContext = useContext(HierarchyContext);

  // Get current links
  const [recentLinks, setRecentLinks] = useState<IRecentLink[] | null>(null);

  useLayoutEffect(() => {
    const storedLinks = window.localStorage.getItem("recentHierarchies") ?? "";
    try {
      const parsedLinks = JSON.parse(storedLinks);
      if (Array.isArray(parsedLinks)) {
        const validLinks = parsedLinks.filter(isIRecentLink);
        startTransition(() => setRecentLinks(validLinks));
      } else {
        startTransition(() => setRecentLinks([]));
      }
    } catch {
      startTransition(() => setRecentLinks([]));
    }
  }, []);

  // Function for minus button press
  const removeRecent = useCallback(
    (id: string) => {
      // Cannot be called while recentLinks is null due to button not rendering
      const rl = recentLinks!.filter((rl) => rl.id !== id);
      window.localStorage.setItem("recentHierarchies", JSON.stringify(rl));
      setRecentLinks(rl);
    },
    [recentLinks],
  );

  // Update links list
  useEffect(() => {
    // Add most recent value
    if (
      hierarchyContext &&
      hierarchyContext.hierarchy &&
      hierarchyContext.hierarchy.id &&
      hierarchyContext.hierarchy.name &&
      hierarchyContext.hierarchy.type &&
      !hierarchyContext.hierarchy.isBackupVersion &&
      recentLinks &&
      (recentLinks.length === 0 ||
        (recentLinks.length > 0 &&
          (hierarchyContext.hierarchy.id !== recentLinks[0].id ||
            hierarchyContext.hierarchy.name !== recentLinks[0].name)))
    ) {
      const newPath = getHierarchyBaseUrl(hierarchyContext.hierarchy);
      const newRecentLinks = [
        {
          id: hierarchyContext.hierarchy.id,
          name: hierarchyContext.hierarchy.name,
          type: hierarchyContext.hierarchy.type,
          path: newPath,
        },
        ...recentLinks
          .filter((h) => h.id !== hierarchyContext.hierarchy!.id && h.path !== newPath)
          .map((h) => {
            // Check for name updates
            const hnchk = hierarchyContext.hierarchyNodes.find((hn) => hn.id === h.id);
            if (hnchk && hnchk.name !== h.name) {
              return {
                ...h,
                name: hnchk.name,
                path: getHierarchyBaseUrl(hnchk),
              };
            } else {
              return h;
            }
          }),
      ];

      // Save back to storage
      window.localStorage.setItem("recentHierarchies", JSON.stringify(newRecentLinks));
      startTransition(() => setRecentLinks(newRecentLinks.slice(0, 9)));
    }
  }, [hierarchyContext, recentLinks]);

  // Return header and links
  return !recentLinks || recentLinks.length === 0 ? null : (
    <>
      <h5>Recently viewed</h5>
      {recentLinks.map((h, i) => (
        <div
          key={`${i}-${h.id}`}
          style={{ display: "flex", flexDirection: "row", flexWrap: "nowrap" }}
        >
          <div style={{ marginRight: "4px", width: "16px" }}>
            <DashCircleDotted
              aria-label={`Remove recent link to ${h.name}`}
              id={`recentlinks-remove-${h.name}`}
              onClick={() => removeRecent(h.id)}
              style={{
                color: "var(--bs-danger)",
              }}
            />
          </div>
          <ActiveNavLink
            id={`recentlinks-link-${h.name}`}
            to={h.path}
            style={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {h.name}
          </ActiveNavLink>
        </div>
      ))}
    </>
  );
};

RecentLinks.displayName = "RecentLinks";
